/**
 * PII obfuscation helpers for Dev Mode.
 *
 * These produce realistic-but-fake values so the app can be screenshotted with
 * real vehicles without exposing customers' personal information.
 *
 * Every function is a PURE transform (it always obfuscates). Gating on whether
 * dev mode is active happens in `useObfuscate`, so these stay easy to test.
 *
 * Obfuscation is DETERMINISTIC: the same input always maps to the same fake
 * value. That keeps a screen internally consistent (one customer shows the same
 * fake name everywhere) and stops values flickering on re-render.
 */

// Fictitious names — nothing here should collide with a real customer.
const FIRST_NAMES = [
  'Oliver', 'Charlotte', 'Jack', 'Amelia', 'Noah', 'Isla', 'Leo', 'Ava',
  'George', 'Mia', 'Hunter', 'Willow', 'Lucas', 'Ruby', 'Mason', 'Grace',
  'Ethan', 'Ella', 'Liam', 'Sophie', 'James', 'Harper', 'Cooper', 'Zoe',
  'Max', 'Lily', 'Riley', 'Emily', 'Toby', 'Chloe',
];

const LAST_NAMES = [
  'Wilson', 'Thompson', 'Walker', 'Robinson', 'Harrison', 'Bennett',
  'Fletcher', 'Hayward', 'Sinclair', 'Mercer', 'Ashford', 'Whitcombe',
  'Pemberton', 'Callaghan', 'Waverley', 'Ellison', 'Brookes', 'Kingsley',
  'Marsden', 'Rutherford', 'Stanton', 'Hollis', 'Ainsworth', 'Chadwick',
  'Larkin', 'Prescott', 'Trelawney', 'Vance', 'Wexford', 'Yardley',
];

/** Stable unsigned 32-bit string hash (djb2 xor variant). */
function hashString(value) {
  const s = String(value == null ? '' : value);
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) {
    h = ((h << 5) + h) ^ s.charCodeAt(i);
  }
  return h >>> 0;
}

/** First token of a name string (used to keep first-name/email in sync). */
function firstToken(value) {
  return String(value == null ? '' : value).trim().split(/\s+/)[0] || '';
}

/** Last token of a name string. */
function lastToken(value) {
  const parts = String(value == null ? '' : value).trim().split(/\s+/).filter(Boolean);
  return parts.length > 1 ? parts[parts.length - 1] : '';
}

export function fakeFirstName(seed) {
  return FIRST_NAMES[hashString(`first:${firstToken(seed) || seed}`) % FIRST_NAMES.length];
}

export function fakeLastName(seed) {
  return LAST_NAMES[hashString(`last:${seed}`) % LAST_NAMES.length];
}

/** Obfuscate a full name string ("Bob Smith" -> "Oliver Wilson"). */
export function obfuscateName(fullName) {
  const s = String(fullName == null ? '' : fullName).trim();
  if (!s) return s;
  const first = fakeFirstName(firstToken(s));
  const last = lastToken(s) ? fakeLastName(lastToken(s)) : '';
  return last ? `${first} ${last}` : first;
}

export function obfuscateFirstName(name) {
  const s = String(name == null ? '' : name).trim();
  if (!s) return s;
  return fakeFirstName(s);
}

export function obfuscateLastName(name) {
  const s = String(name == null ? '' : name).trim();
  if (!s) return s;
  return fakeLastName(s);
}

/** Vehicle registration -> "PGQ" + three digits derived from the real rego. */
export function obfuscateRego(rego) {
  const s = String(rego == null ? '' : rego).trim();
  if (!s) return s;
  const digits = String(hashString(s) % 1000).padStart(3, '0');
  return `PGQ${digits}`;
}

/** Panel Quote job number is always flattened to a fixed placeholder. */
export function obfuscateJobNumber() {
  return '123456';
}

/** Phone numbers are always flattened to a fixed placeholder. */
export function obfuscatePhone() {
  return '021123456';
}

/**
 * Email -> "<fake first name>@customeremail.com".
 * Pass the customer's real name as `nameHint` so the fake email matches the
 * fake name shown elsewhere on the screen.
 */
export function obfuscateEmail(email, nameHint) {
  const s = String(email == null ? '' : email).trim();
  if (!s) return s;
  const seed = firstToken(nameHint) || s.split('@')[0] || s;
  return `${fakeFirstName(seed).toLowerCase()}@customeremail.com`;
}

/** VIN -> first three characters, the rest replaced with 'X' (length kept). */
export function obfuscateVin(vin) {
  const s = String(vin == null ? '' : vin).trim();
  if (!s) return s;
  if (s.length <= 3) return s;
  return s.slice(0, 3) + 'X'.repeat(s.length - 3);
}

/** Addresses are simply blanked. */
export function obfuscateAddress() {
  return '';
}
